import React, { useState } from 'react';
import { styled } from '@material-ui/styles';
import { Paper, Box, CircularProgress, useTheme, FormControlLabel, Switch } from '@material-ui/core';
import SpellcheckIcon from '@material-ui/icons/Spellcheck';
import CachedIcon from '@material-ui/icons/Cached';
import { useGenerateDrill } from '../../hooks/useGenerateDrill';
import { useQuota } from '../../hooks/useQuota';
import { handleDrillError } from '../../utils/errorHandler';
import Content from '../content';
import Button from '../button';
import Link from '../link';
import Typography from '../typography';
import { IStyledArguments } from '../../types/styled-arguments';

type TrainingType =
  | 'INDIVIDUAL_TECHNIQUE_1V1'
  | 'COLLECTIVE_TECHNIQUE'
  | 'PHYSICAL_CONDITIONING'
  | 'TACTICAL_FORMATION'
  | 'GOALKEEPING'
  | 'BALL_CONTROL_DRIBBLING'
  | 'PASSING_RECEIVING'
  | 'SHOOTING_FINISHING'
  | 'DEFENSIVE_POSITIONING'
  | 'SET_PIECES';

type AgeGroup = 'U8' | 'U10' | 'U12' | 'U14' | 'U16' | 'U18' | 'ADULT';

const PageContainer = styled('div')(({ theme }: IStyledArguments) => ({
  minHeight: '100vh',
  background: theme.palette.extra.backgroundGrey,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(10),
  },
}));

// Header Section
const HeaderSection = styled('div')(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const HeaderContent = styled(Box)(({ theme }: IStyledArguments) => ({
  maxWidth: 1200,
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const HeaderIcon = styled('div')(({ theme }: IStyledArguments) => ({
  width: 56,
  height: 56,
  borderRadius: '16px',
  background: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '1.8rem',
    color: '#fff',
  },
  [theme.breakpoints.down('sm')]: {
    width: 48,
    height: 48,
    '& svg': {
      fontSize: '1.5rem',
    },
  },
}));

const HeaderTitle = styled(Typography.H4)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: '1.8rem',
  letterSpacing: '-0.5px',
  margin: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.4rem',
  },
}));

// Styled Paper Container
const StyledPaper = styled(Paper)(({ theme }: IStyledArguments) => ({
  boxSizing: 'border-box',
  padding: theme.spacing(5, 6),
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
  maxWidth: 1200,
  borderRadius: '18px',
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: '0 1px 3px rgba(16, 21, 26, 0.06)',
  [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
    borderRadius: '16px',
    padding: theme.spacing(3, 2.5),
    margin: theme.spacing(0, 2),
  },
}));

// Form Section Container
const FormSection = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: '18px',
  border: `1px solid ${theme.palette.extra.borderColor}`,
  padding: theme.spacing(3),
  gap: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(3),
}));

// Styled Label
const StyledLabel = styled('label')(({ theme }: IStyledArguments) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  fontSize: '0.95rem',
  display: 'block',
  marginBottom: theme.spacing(1),
}));

// Select/Input planos
const StyledInput = styled('input')(({ theme }: IStyledArguments) => ({
  width: '100%',
  boxSizing: 'border-box',
  background: theme.palette.background.paper,
  borderRadius: '10px',
  padding: theme.spacing(1.5),
  border: `1.5px solid ${theme.palette.extra.borderColor}`,
  fontFamily: 'inherit',
  fontSize: '1rem',
  color: theme.palette.text.primary,
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
  },
}));

const StyledSelect = styled('select')(({ theme }: IStyledArguments) => ({
  width: '100%',
  boxSizing: 'border-box',
  background: theme.palette.background.paper,
  borderRadius: '10px',
  padding: theme.spacing(1.5),
  border: `1.5px solid ${theme.palette.extra.borderColor}`,
  fontFamily: 'inherit',
  fontSize: '1rem',
  color: theme.palette.text.primary,
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
  },
}));

// Card de cuota de IA — acento plano, sin degradado ni glow
const QuotaCard = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  borderRadius: '18px',
  padding: theme.spacing(3, 3.5),
  marginBottom: theme.spacing(4),
  color: '#fff',
}));

const QuotaHeader = styled('div')(({ theme }: IStyledArguments) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2.5),
  paddingBottom: theme.spacing(2),
  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
}));

const QuotaPlanBadge = styled('span')(({ theme }: IStyledArguments) => ({
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'rgba(255, 255, 255, 0.25)',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: '12px',
}));

const QuotaTitle = styled('h3')({
  fontSize: '1.2rem',
  fontWeight: 700,
  margin: 0,
});

const QuotaGrid = styled('div')(({ theme }: IStyledArguments) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const QuotaMetric = styled('div')(({ theme }: IStyledArguments) => ({
  background: 'rgba(255, 255, 255, 0.15)',
  borderRadius: '14px',
  padding: theme.spacing(2),
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '& .label': {
    fontSize: '0.85rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    opacity: 0.9,
    marginBottom: theme.spacing(0.75),
    display: 'block',
  },
  '& .value': {
    fontSize: '1.5rem',
    fontWeight: 800,
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
  },
  '& .max': {
    fontSize: '1rem',
    opacity: 0.8,
    fontWeight: 500,
  },
}));

const QuotaMessage = styled('div')(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(1.5, 2),
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '12px',
  fontSize: '0.9rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& .icon': {
    fontSize: '1.2rem',
  },
}));

// Botón de envío
const SubmitButton = styled(Button)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: '#fff',
  fontWeight: 700,
  borderRadius: '14px',
  padding: theme.spacing(2, 4),
  textTransform: 'none',
  fontSize: '1.05rem',
  transition: 'background 0.2s ease',
  width: '100%',
  '&:hover': {
    background: theme.palette.primary.dark,
  },
  '&:disabled': {
    background: 'rgba(200, 200, 200, 0.6)',
    color: 'rgba(0, 0, 0, 0.3)',
  },
}));

// Loading Container
const LoadingContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60vh',
  gap: theme.spacing(3),
}));

const LoadingText = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '1.1rem',
}));

// Resultado del drill generado
const DrillResultContainer = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: '18px',
  border: `1px solid ${theme.palette.extra.borderColor}`,
  padding: theme.spacing(4),
  marginTop: theme.spacing(3),
}));

const DrillTitle = styled(Typography.H5)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: '1.8rem',
  letterSpacing: '-0.5px',
  marginBottom: theme.spacing(2),
}));

const DevelopmentStep = styled(Box)(({ theme }: IStyledArguments) => ({
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  paddingLeft: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '& h5': {
    fontWeight: 700,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  '& p': {
    color: theme.palette.text.secondary,
    margin: 0,
  },
}));

const SectionTitle = styled('h4')(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.primary.main,
  fontSize: '1.1rem',
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(2.5),
}));

const StyledList = styled('ul')(({ theme }: IStyledArguments) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  '& li': {
    padding: theme.spacing(1),
    paddingLeft: 0,
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
    '&:before': {
      content: '"•"',
      color: theme.palette.primary.main,
      fontWeight: 'bold',
      marginRight: theme.spacing(1),
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
}));

// Alerta de error
const ErrorAlert = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.error.light,
  borderRadius: '14px',
  border: `1px solid ${theme.palette.error.main}`,
  padding: theme.spacing(2, 3),
  marginTop: theme.spacing(2),
  color: theme.palette.error.main,
  fontWeight: 600,
}));

export function DrillGenerator() {
  const theme = useTheme();
  const { generateDrill, drill, loading, error } = useGenerateDrill() as any;
  const { quota, loading: quotaLoading } = useQuota() as any;

  // Debug: log quota info from hook to verify frontend reads backend response
  /* istanbul ignore next */
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[DrillGenerator] quota:', quota);
    console.debug('[DrillGenerator] quota.canGenerate:', quota?.canGenerate);
  }

  const [formData, setFormData] = useState({
    trainingType: 'PASSING_RECEIVING' as TrainingType,
    ageGroup: 'U12' as AgeGroup,
    duration: '15 minutes',
    numberOfPlayers: 12,
    spaceAvailable: 'Half field',
    saveToLibrary: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!quota?.canGenerate) {
      return;
    }

    try {
      await generateDrill({ variables: { input: formData } });
    } catch (err: any) {
      const friendly = handleDrillError(err);
      if (friendly.action === 'upgrade') {
        window.location.href = '/upgrade';
      } else if (friendly.action === 'login') {
        window.location.href = '/login';
      }
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Content>
          <StyledPaper elevation={0}>
            <LoadingContainer>
              <CachedIcon style={{ fontSize: 56, color: theme.palette.primary.main, animation: 'spin 2s linear infinite' }} />
              <LoadingText>Generating your AI drill...</LoadingText>
            </LoadingContainer>
          </StyledPaper>
        </Content>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderSection>
        <HeaderContent>
          <HeaderIcon>
            <SpellcheckIcon />
          </HeaderIcon>
          <HeaderTitle>AI Drill Generator</HeaderTitle>
        </HeaderContent>
      </HeaderSection>

      <Content>
        <StyledPaper elevation={0}>
          {/* Quota Display */}
          {!quotaLoading && quota && (
            <QuotaCard>
              <QuotaHeader>
                <QuotaTitle>AI Credits</QuotaTitle>
                <QuotaPlanBadge>{quota.subscriptionTier}</QuotaPlanBadge>
              </QuotaHeader>
              <QuotaGrid>
                <QuotaMetric>
                  <span className="label">Today (available)</span>
                  <span className="value">{quota.remainingToday} of {quota.dailyLimit} available</span>
                </QuotaMetric>
                <QuotaMetric>
                  <span className="label">This Month (available)</span>
                  <span className="value">{quota.remainingThisMonth} of {quota.monthlyLimit} available</span>
                </QuotaMetric>
              </QuotaGrid>
              {!quota.canGenerate ? (
                <div style={{ fontSize: '0.9rem', fontWeight: 600, opacity: 0.9 }}>
                  ⚠️ Quota reached. Upgrade to continue generating drills.
                </div>
              ) : (
                <QuotaMessage>
                  <span className="icon">✅</span>
                  You can generate drills. Each generation uses 1 credit.
                </QuotaMessage>
              )}
            </QuotaCard>
          )}

          {/* Generation Form */}
          <form onSubmit={handleSubmit}>
            <FormSection>
              <div>
                <StyledLabel htmlFor="trainingType">Training Type</StyledLabel>
                <StyledSelect
                  id="trainingType"
                  value={formData.trainingType}
                  onChange={(e) => setFormData({ ...formData, trainingType: e.target.value as TrainingType })}
                >
                  <option value="INDIVIDUAL_TECHNIQUE_1V1">Individual Technique 1v1</option>
                  <option value="COLLECTIVE_TECHNIQUE">Collective Technique</option>
                  <option value="PHYSICAL_CONDITIONING">Physical Conditioning</option>
                  <option value="TACTICAL_FORMATION">Tactical Formation</option>
                  <option value="GOALKEEPING">Goalkeeping</option>
                  <option value="BALL_CONTROL_DRIBBLING">Ball Control & Dribbling</option>
                  <option value="PASSING_RECEIVING">Passing & Receiving</option>
                  <option value="SHOOTING_FINISHING">Shooting & Finishing</option>
                  <option value="DEFENSIVE_POSITIONING">Defensive Positioning</option>
                  <option value="SET_PIECES">Set Pieces</option>
                </StyledSelect>
              </div>

              <div>
                <StyledLabel htmlFor="ageGroup">Age Group</StyledLabel>
                <StyledSelect
                  id="ageGroup"
                  value={formData.ageGroup}
                  onChange={(e) => setFormData({ ...formData, ageGroup: e.target.value as AgeGroup })}
                >
                  <option value="U8">U8</option>
                  <option value="U10">U10</option>
                  <option value="U12">U12</option>
                  <option value="U14">U14</option>
                  <option value="U16">U16</option>
                  <option value="U18">U18</option>
                  <option value="ADULT">Adult</option>
                </StyledSelect>
              </div>

              <div>
                <StyledLabel htmlFor="duration">Duration</StyledLabel>
                <StyledInput
                  id="duration"
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 15 minutes"
                />
              </div>

              <div>
                <StyledLabel htmlFor="numberOfPlayers">Number of Players</StyledLabel>
                <StyledInput
                  id="numberOfPlayers"
                  type="number"
                  value={formData.numberOfPlayers}
                  onChange={(e) => setFormData({ ...formData, numberOfPlayers: parseInt(e.target.value || '0') })}
                  min={4}
                  max={30}
                />
              </div>

              <div>
                <StyledLabel htmlFor="spaceAvailable">Space Available</StyledLabel>
                <StyledSelect
                  id="spaceAvailable"
                  value={formData.spaceAvailable}
                  onChange={(e) => setFormData({ ...formData, spaceAvailable: e.target.value })}
                >
                  <option value="Full field">Full field</option>
                  <option value="Half field">Half field</option>
                  <option value="Third of field">Third of field</option>
                  <option value="Penalty area">Penalty area</option>
                  <option value="Small grid (20x20)">Small grid (20x20)</option>
                  <option value="Medium grid (30x30)">Medium grid (30x30)</option>
                  <option value="Large grid (40x40)">Large grid (40x40)</option>
                </StyledSelect>
              </div>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.saveToLibrary}
                    onChange={(e) => setFormData({ ...formData, saveToLibrary: e.target.checked })}
                    color="primary"
                  />
                }
                label="Save to Library"
                style={{ color: theme.palette.primary.main, fontWeight: 600 }}
              />
            </FormSection>

            <SubmitButton
              type="submit"
              disabled={
                loading || !(
                  quota &&
                  quota.canGenerate &&
                  (quota.remainingToday ?? 0) > 0 &&
                  (quota.remainingThisMonth ?? 0) > 0
                )
              }
              aria-disabled={
                loading || !(
                  quota &&
                  quota.canGenerate &&
                  (quota.remainingToday ?? 0) > 0 &&
                  (quota.remainingThisMonth ?? 0) > 0
                )
              }
            >
              {loading ? 'Generating...' : 'Generate Drill'}
            </SubmitButton>
          </form>

          {/* Error Display */}
          {error && (
            <ErrorAlert>
              <strong>Error:</strong> {error.message}
            </ErrorAlert>
          )}

          {/* Generated Drill Display */}
          {drill && (
            <DrillResultContainer>
              <DrillTitle>{drill.title}</DrillTitle>
              <p style={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                {drill.description}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing(2), marginBottom: theme.spacing(2) }}>
                <Box sx={{ background: theme.palette.extra.accentSoft, borderRadius: '12px', padding: theme.spacing(2) }}>
                  <span style={{ fontSize: '0.85rem', color: theme.palette.text.secondary, fontWeight: 600 }}>
                    Age Group
                  </span>
                  <p style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.palette.primary.main, margin: 0 }}>
                    {drill.ageGroup}
                  </p>
                </Box>
                <Box sx={{ background: theme.palette.extra.accentSoft, borderRadius: '12px', padding: theme.spacing(2) }}>
                  <span style={{ fontSize: '0.85rem', color: theme.palette.text.secondary, fontWeight: 600 }}>
                    Duration
                  </span>
                  <p style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.palette.primary.main, margin: 0 }}>
                    {drill.duration}
                  </p>
                </Box>
              </div>

              {drill.objectives && (
                <>
                  <SectionTitle>Objectives</SectionTitle>
                  <StyledList>
                    {drill.objectives.map((obj: string, i: number) => (
                      <li key={i}>{obj}</li>
                    ))}
                  </StyledList>
                </>
              )}

              {drill.equipment && (
                <>
                  <SectionTitle>Equipment</SectionTitle>
                  <StyledList>
                    {drill.equipment.map((eq: string, i: number) => (
                      <li key={i}>{eq}</li>
                    ))}
                  </StyledList>
                </>
              )}

              {drill.development && (
                <>
                  <SectionTitle>Development</SectionTitle>
                  {drill.development.map((step: any) => (
                    <DevelopmentStep key={step.step}>
                      <h5>Step {step.step} {step.duration && `(${step.duration})`}</h5>
                      <p>{step.description}</p>
                    </DevelopmentStep>
                  ))}
                </>
              )}

              {drill.coachingPoints && (
                <>
                  <SectionTitle>Coaching Points</SectionTitle>
                  <StyledList>
                    {drill.coachingPoints.map((point: string, i: number) => (
                      <li key={i}>{point}</li>
                    ))}
                  </StyledList>
                </>
              )}

              {drill.variations && (
                <>
                  <SectionTitle>Variations</SectionTitle>
                  <StyledList>
                    {drill.variations.map((variation: string, i: number) => (
                      <li key={i}>{variation}</li>
                    ))}
                  </StyledList>
                </>
              )}
            </DrillResultContainer>
          )}
        </StyledPaper>
      </Content>
    </PageContainer>
  );
}
